import React, { useState } from 'react';
import { Box, Card, CardHeader, CardBody, Heading, Text, Button, Flex, Icon, Badge, useToast, Divider, Textarea } from '@chakra-ui/react';
import { FiAlertCircle, FiCheck, FiX, FiEdit2 } from 'react-icons/fi';
import FqPaClaimDetailShell from './FqPaClaimDetailShell';

const mockData = {
  type: 'Pre-Submission',
  patient: { first_name: 'James', last_name: 'Wilson', member_id: 'KF663018257' },
  provider: { name: 'David T. Nguyen, MD', npi: '1902847561' },
  claim: {
    diagnoses: ['S61.212A'], // Laceration without foreign body of left index finger
    procedures: [
      { cpt: 'T1015', modifiers: [], charge: 186.00 }, // FQHC encounter \u2014 second same-day medical (flagged)
      { cpt: '12001', modifiers: [], charge: 0.00 }  // Simple laceration repair (zero-pay tracking line)
    ],
    clinical_note: "Patient returned to clinic at 4:30 PM after sustaining a deep laceration to the left index finger while chopping vegetables at home. Patient was seen earlier today at 9:00 AM for a routine hypertension follow-up. Wound cleaned and closed with 4 sutures. Tolerated procedure well."
  }
};

const FqPaDemoBox80 = () => {
  const [isApproved, setIsApproved] = useState(false);
  const [box80Text, setBox80Text] = useState("Patient seen earlier today for routine HTN follow-up. Returned later same day with new acute injury (laceration to left index finger requiring sutures). Distinct illness/injury subsequent to the first encounter; second medical encounter justified per the PA FQHC/RHC PROMISe Provider Handbook.");
  const toast = useToast();

  const handleApprove = () => {
    setIsApproved(true);
    toast({
      title: "Box 80 Appended",
      description: "Justification added to claim. Claim cleared for submission.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const rightPanel = (
    <Card borderColor="purple.200" borderWidth="2px" shadow="md">
      <CardHeader bg="purple.50" pb={4}>
        <Flex justify="space-between" align="center">
          <Heading size="md" display="flex" alignItems="center" color="purple.800">
            <Icon as={FiAlertCircle} mr={2} />
            Revedy Action Required
          </Heading>
          <Badge colorScheme="purple" variant="solid">Tier B</Badge>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box mb={4}>
          <Text fontWeight="bold" color="orange.600" mb={1}>Scrub Error (Duplicate Same-Type Encounter):</Text>
          <Text fontSize="sm">A medical PPS encounter (T1015) has already been billed for this patient on this date of service. Two encounters of the same type on one date are not separately payable without justification.</Text>
        </Box>
        
        <Divider my={4} />

        <Box mb={4}>
          <Text fontWeight="bold" color="purple.700" mb={1}>Revedy Analysis:</Text>
          <Text fontSize="sm" mb={2}>
            The clinical note confirms a distinct, later-day injury (laceration at 4:30 PM) after this morning's scheduled HTN follow-up \u2014 not a split visit. PA pays a second same-type encounter when a subsequent illness or injury requires additional diagnosis or treatment; document it in the UB-04 remarks (Box 80). Note: if the second visit had been behavioral health or dental, no justification would be needed \u2014 PA pays different-type same-day encounters separately.
          </Text>
        </Box>

        <Box mb={4} p={3} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="purple.400">
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Rule Citation:</Text>
          <Text fontSize="sm" fontStyle="italic">"A second encounter is reimbursable when the patient suffers an illness or injury subsequent to the first encounter that requires additional diagnosis or treatment." (PA FQHC/RHC PROMISe Provider Handbook; see also DHS FQHC/RHC FAQ on same-day encounters)</Text>
        </Box>

        <Box mb={6}>
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Proposed Box 80 Remarks:</Text>
          <Textarea 
            value={box80Text}
            onChange={(e) => setBox80Text(e.target.value)}
            size="sm"
            rows={4}
            isDisabled={isApproved}
          />
        </Box>

        <Flex gap={3}>
          <Button 
            flex="1" 
            colorScheme="purple" 
            leftIcon={<FiCheck />} 
            onClick={handleApprove}
            isDisabled={isApproved}
          >
            {isApproved ? 'Approved' : 'Approve & Append'}
          </Button>
          <Button variant="ghost" colorScheme="red" isDisabled={isApproved}>
            <Icon as={FiX} />
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );

  return <FqPaClaimDetailShell claimData={mockData} rightPanel={rightPanel} backTo="/fq-pa/pre-submission" />;
};

export default FqPaDemoBox80;